(() => {
  if (window.WNProductCare) return;

  const selector = '[data-wn-refill-care]';
  const controllers = new WeakMap();

  const numberFromId = (value) => Number(String(value || '').split('/').pop()) || 0;

  const careTier = (item) => {
    const property = String(item?.properties?.['_Wandersnout Filter Care'] || '').toLowerCase();
    const handle = String(item?.handle || item?.product_handle || '').toLowerCase();
    if (property === 'care+' || handle === 'nami-filter-care-enrollment' || handle === 'nami-filter-care-plus') return 'care-plus';
    if (property === 'care' || handle === 'nami-filter-care') return 'care';
    return null;
  };

  const sellingPlanId = (item) => numberFromId(
    item?.selling_plan_allocation?.selling_plan?.id
      || item?.selling_plan_allocation?.selling_plan_id
      || item?.selling_plan
  );

  const normalizeChoice = (value) => {
    if (value === 'care-plus' || value === 'plus') return 'care-plus';
    if (value === 'care') return 'care';
    if (value === 'filters-12') return 'filters-12';
    if (value === 'filters-6' || value === 'filters-only' || value === 'filters') return 'filters-6';
    if (value === 'one-time' || value === 'starter') return 'one-time';
    return 'none';
  };

  class ProductCareController {
    constructor(node) {
      this.node = node;
      this.host = node.closest('wn-product-form');
      if (!this.host) return;

      this.abortController = new AbortController();
      this.variants = this.readVariants();
      this.productKey = this.readProductKey();
      this.starter = this.findChoice('one-time');
      this.panel = node.querySelector('[data-wn-care-upgrades]');
      this.selectedSummary = node.querySelector('[data-wn-filtercare-selected]');
      this.ensureSelectedSummary();
      this.bind();
      this.select(this.starter || this.findChoice('none') || node.querySelector('[data-wn-filter-choice]'));
      node.dataset.wnCareOwner = 'product-care';
      node.getPurchaseIntent = (context) => this.getPurchaseIntent(context);
      node.composePurchase = (context) => this.composePurchase(context);
    }

    readVariants() {
      try {
        return JSON.parse(this.host.querySelector('[data-product-variants]')?.textContent || '[]');
      } catch {
        return [];
      }
    }

    readProductKey() {
      try {
        return new URL(this.host.dataset.productUrl || location.pathname, location.origin).pathname.replace(/\/$/, '') || '/';
      } catch {
        return String(this.host.dataset.productUrl || location.pathname).replace(/\/$/, '') || '/';
      }
    }

    ensureSelectedSummary() {
      if (this.selectedSummary) return;
      const summaryCopy = this.panel?.querySelector('.wn-refill-care__upgrade-summary-copy');
      if (!summaryCopy) return;
      this.selectedSummary = document.createElement('span');
      this.selectedSummary.className = 'wn-filtercare__selected';
      this.selectedSummary.dataset.wnFiltercareSelected = '';
      this.selectedSummary.hidden = true;
      summaryCopy.appendChild(this.selectedSummary);
    }

    bind() {
      const { signal } = this.abortController;
      this.node.addEventListener('click', (event) => {
        const card = event.target.closest('[data-wn-filter-choice-card]');
        const input = card?.querySelector('[data-wn-filter-choice]');
        if (!input || input.disabled || event.target.matches('[data-wn-filter-choice]') || event.target.closest('summary,a,button,details details')) return;
        event.preventDefault();
        this.select(input, { collapse: this.isAddon(this.choice(input)) });
      }, { signal });

      this.node.addEventListener('change', (event) => {
        if (!event.target.matches('[data-wn-filter-choice]')) return;
        this.select(event.target, { collapse: this.isAddon(this.choice(event.target)) });
      }, { signal });

      this.host.addEventListener('wn:product:variant-change', () => this.renderPrice(), { signal });
      this.host.addEventListener('wn:product:add-start', () => this.node.setAttribute('aria-busy', 'true'), { signal });
      this.host.addEventListener('wn:product:add-success', () => this.finishSubmit(), { signal });
      this.host.addEventListener('wn:product:add-error', (event) => this.finishSubmit(event.detail?.error?.message), { signal });

      document.addEventListener('pointerdown', (event) => {
        if (this.panel?.open && !this.panel.contains(event.target)) this.panel.open = false;
      }, { passive: true, signal });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.panel?.open) return;
        this.panel.open = false;
        this.panel.querySelector(':scope > summary')?.focus();
      }, { signal });
    }

    destroy() {
      this.abortController.abort();
      delete this.node.getPurchaseIntent;
      delete this.node.composePurchase;
      delete this.node.dataset.wnCareOwner;
      controllers.delete(this.node);
    }

    finishSubmit(message = '') {
      this.node.removeAttribute('aria-busy');
      const status = this.node.querySelector('[data-wn-refill-care-status]');
      if (status) status.textContent = message || '';
    }

    findChoice(value) {
      return this.node.querySelector(`[data-wn-filter-choice="${value}"]`);
    }

    choice(input) {
      return normalizeChoice(input?.dataset.wnFilterChoice);
    }

    checkedChoice() {
      return this.node.querySelector('[data-wn-filter-choice]:checked') || this.starter || this.findChoice('none');
    }

    currentVariant() {
      const selectedId = this.host.querySelector('[data-variant-id]')?.value;
      return this.host.currentVariant
        || this.variants.find((variant) => String(variant.id) === String(selectedId))
        || this.variants[0];
    }

    isFilterOnly(mode) {
      return mode === 'filters-6' || mode === 'filters-12';
    }

    isAddon(mode) {
      return this.isFilterOnly(mode) || mode === 'care' || mode === 'care-plus';
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR'
        }).format(cents / 100);
      } catch {
        return `€${(cents / 100).toFixed(2)}`;
      }
    }

    totalFor(input, variant) {
      const extra = Number(input?.dataset.priceTodayCents || 0);
      return this.isFilterOnly(this.choice(input)) ? extra : Number(variant.price) + extra;
    }

    referenceFor(input, variant) {
      const mode = this.choice(input);
      const total = this.totalFor(input, variant);
      const compareExtra = Number(input?.dataset.compareTodayCents || 0);
      if (mode === 'one-time') return Number(variant.price) + compareExtra;
      if (this.isFilterOnly(mode)) return Math.max(total, compareExtra || total);
      return Math.max(Number(variant.compare_at_price) || 0, Number(variant.price))
        + Number(input?.dataset.compareTodayCents || input?.dataset.priceTodayCents || 0);
    }

    stateFor(input, variant) {
      const mode = this.choice(input);
      const total = this.totalFor(input, variant);
      const reference = this.referenceFor(input, variant);
      const label = input?.dataset.selectionLabel || '';
      const title = String(variant?.title || '');
      const displayLabel = this.isFilterOnly(mode) ? label : title && title !== 'Default Title' ? `${title} · ${label}` : label;
      const purchaseVariant = Number(input?.dataset.filterPurchaseVariantId || 0);
      return {
        mode,
        label,
        displayLabel,
        priceCents: total,
        priceFormatted: this.money(total),
        compareCents: reference > total ? reference : 0,
        compareFormatted: reference > total ? this.money(reference) : '',
        available: !input?.disabled && (this.isFilterOnly(mode) ? Boolean(purchaseVariant) : Boolean(variant?.available)),
        productUrl: this.productKey
      };
    }

    emitState(input, variant) {
      const state = this.stateFor(input, variant);
      window.__WN_NAMI_PURCHASE_STATE__ ||= {};
      window.__WN_NAMI_PURCHASE_STATE__[this.productKey] = state;
      document.dispatchEvent(new CustomEvent('wn:nami:purchase-state-change', { detail: { productUrl: this.productKey, state } }));
    }

    updateSummary(input) {
      if (!this.selectedSummary) return;
      const mode = this.choice(input);
      if (!this.isAddon(mode)) {
        this.selectedSummary.hidden = true;
        this.selectedSummary.textContent = '';
        return;
      }
      const locale = (document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
      const prefix = { nl: 'Geselecteerd', en: 'Selected', de: 'Ausgewählt', fr: 'Sélectionné', es: 'Seleccionado', it: 'Selezionato' }[locale] || 'Selected';
      const label = input?.dataset.selectionLabel || '';
      this.selectedSummary.textContent = label ? `${prefix}: ${label}` : '';
      this.selectedSummary.hidden = !label;
    }

    renderPrice() {
      const variant = this.currentVariant();
      if (!variant) return;
      const input = this.checkedChoice();
      const total = this.totalFor(input, variant);
      const reference = this.referenceFor(input, variant);
      const current = this.host.querySelector('[data-price-current]');
      const compare = this.host.querySelector('[data-price-compare]');
      if (current) current.textContent = this.money(total);
      if (compare) {
        compare.hidden = reference <= total;
        compare.textContent = compare.hidden ? '' : this.money(reference);
      }
      this.node.querySelectorAll('[data-wn-filter-choice]').forEach((choice) => {
        const card = choice.closest('[data-wn-filter-choice-card]');
        const choiceTotal = this.totalFor(choice, variant);
        const choiceReference = this.referenceFor(choice, variant);
        card?.querySelectorAll('[data-wn-choice-total],[data-wn-choice-detail-total]')
          .forEach((element) => { element.textContent = this.money(choiceTotal); });
        card?.querySelectorAll('[data-wn-choice-compare]')
          .forEach((element) => {
            element.hidden = choiceReference <= choiceTotal;
            element.textContent = element.hidden ? '' : this.money(choiceReference);
          });
      });
      this.updateSummary(input);
      this.emitState(input, variant);
    }

    select(value, { collapse = false } = {}) {
      const input = typeof value === 'string' ? this.findChoice(value) : value;
      if (!input) return;
      const mode = this.choice(input);
      this.node.querySelectorAll('[data-wn-filter-choice]').forEach((choice) => {
        choice.checked = choice === input;
        choice.closest('[data-wn-filter-choice-card]')?.classList.toggle('is-selected', choice === input);
      });
      this.node.querySelectorAll('[data-wn-subscription-detail]').forEach((detail) => {
        detail.hidden = detail.dataset.wnSubscriptionDetail !== input.dataset.wnFilterChoice;
      });
      this.renderPrice();
      if (this.panel && collapse && this.isAddon(mode)) window.setTimeout(() => { this.panel.open = false; }, 110);
    }

    getPurchaseIntent({ formData, variant }) {
      const input = this.checkedChoice();
      const mode = this.choice(input);
      if (mode === 'none') return null;

      const main = {
        id: Number(formData.get('id') || variant.id),
        quantity: Math.max(1, Number(formData.get('quantity')) || 1)
      };
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '' && ((mode === 'care' || mode === 'care-plus') || match[1] !== '_Filter choice')) properties[match[1]] = value;
      }
      if (Object.keys(properties).length) main.properties = properties;

      const plus = mode === 'care-plus';
      return {
        mode,
        main,
        filterVariantId: Number(input?.dataset.filterPurchaseVariantId || 0),
        subscriptionVariantId: Number(plus ? this.node.dataset.carePlusVariantId : this.node.dataset.careVariantId) || 0,
        sellingPlanId: Number(plus ? this.node.dataset.carePlusSellingPlanId : this.node.dataset.careSellingPlanId) || 0
      };
    }

    composePurchase({ cart, intent }) {
      if (!Array.isArray(cart?.items)) throw new Error('Cart state unavailable');

      const normalFilterId = Number(this.node.dataset.filterVariantId || 0);
      const filter12Id = Number(this.node.dataset.filter12VariantId || 0);
      const starterId = Number(this.node.dataset.starterVariantId || 0);
      const careStarterId = Number(this.node.dataset.careStarterVariantId || 0);
      const managed = [normalFilterId, filter12Id, starterId, careStarterId].filter(Boolean);
      const updates = {};

      if (this.isFilterOnly(intent.mode)) {
        if (!intent.filterVariantId) throw new Error('Filter configuration missing');
        cart.items.forEach((item) => {
          if (managed.includes(Number(item.variant_id)) || careTier(item)) updates[item.key] = 0;
        });
        return {
          updates,
          input: { items: [{ id: intent.filterVariantId, quantity: 1 }] },
          source: intent.mode === 'filters-12' ? 'nami-filters-12' : 'nami-filters-6'
        };
      }

      const wantsCare = intent.mode === 'care' || intent.mode === 'care-plus';
      const desiredId = wantsCare ? careStarterId : starterId;
      if (!desiredId) throw new Error('Filter starter configuration missing');
      if (wantsCare && (!intent.subscriptionVariantId || !intent.sellingPlanId)) throw new Error('Filter Care configuration missing');

      const desired = cart.items.filter((item) => Number(item.variant_id) === desiredId);
      cart.items.forEach((item) => {
        if (managed.includes(Number(item.variant_id)) && Number(item.variant_id) !== desiredId) updates[item.key] = 0;
      });
      if (desired[0] && desired[0].quantity !== 1) updates[desired[0].key] = 1;
      desired.slice(1).forEach((item) => { updates[item.key] = 0; });

      const subscriptions = cart.items.filter((item) => careTier(item));
      const targetTier = intent.mode;
      const matchesTarget = (item) => careTier(item) === targetTier && sellingPlanId(item) === intent.sellingPlanId;
      if (wantsCare) {
        const valid = subscriptions.filter(matchesTarget);
        subscriptions.filter((item) => !matchesTarget(item)).forEach((item) => { updates[item.key] = 0; });
        if (valid[0] && valid[0].quantity !== 1) updates[valid[0].key] = 1;
        valid.slice(1).forEach((item) => { updates[item.key] = 0; });
      } else {
        subscriptions.forEach((item) => { updates[item.key] = 0; });
      }

      const items = [intent.main];
      if (!desired.length) {
        items.push(wantsCare
          ? { id: desiredId, quantity: 1, properties: { '_Wandersnout Care Starter': 'true' } }
          : { id: desiredId, quantity: 1, properties: { '_Wandersnout Nami Add-on': 'Starter filters' } });
      }
      if (wantsCare && !subscriptions.some(matchesTarget)) {
        items.push({
          id: intent.subscriptionVariantId,
          quantity: 1,
          selling_plan: intent.sellingPlanId,
          properties: {
            '_Wandersnout Filter Care': intent.mode === 'care-plus' ? 'Care+' : 'Care',
            '_Do not fulfill': 'Virtual subscription enrollment'
          }
        });
      }

      return {
        updates,
        input: { items },
        source: wantsCare ? 'nami-care' : 'nami-complete-starter'
      };
    }
  }

  function boot(root = document) {
    root.querySelectorAll(selector).forEach((node) => {
      if (controllers.has(node)) return;
      const controller = new ProductCareController(node);
      if (controller.host) controllers.set(node, controller);
    });
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(selector).forEach((node) => controllers.get(node)?.destroy());
  });
  window.WNProductCare = Object.freeze({ boot });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
})();
